import React, { useState } from 'react';
import { tutorialData } from 'enums';
import { ChevronDown, ExternalLink, Play } from 'lucide-react';

const TutorialPage = () => {
  // First tutorial is open by default (index 0)
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-ink text-base">
            Learn how to get the most out of NirvaScribe with our step-by-step video guides
          </p>
        </div>

        {/* Tutorial Sections */}
        <div data-reveal-group className="space-y-4">
          {tutorialData.map((section, sectionIndex) => {
            const globalIndex = tutorialData
              .slice(0, sectionIndex)
              .reduce((acc, s) => acc + s.tutorials.length, 0);

            return section.tutorials.map((tutorial, tutorialIndex) => {
              const currentIndex = globalIndex + tutorialIndex;
              const isOpen = openIndex === currentIndex;

              return (
                <div
                  key={currentIndex}
                  className={`bg-white rounded-ns-tile  transition-all duration-300 overflow-hidden ${
                    isOpen ? 'ring-2 ring-accent' : 'hover:shadow-pop'
                  }`}
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => handleToggle(currentIndex)}
                    className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-shade-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div
                        className={`p-3 rounded-ns transition-colors ${
                          isOpen ? 'bg-shade-50' : 'bg-shade-50'
                        }`}
                      >
                        <Play
                          className={`w-5 h-5 ${
                            isOpen ? 'text-ink' : 'text-muted'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-bold text-ink mb-1">
                          {section.sectionTitle}
                        </div>
                        <h3 className="text-sm font-bold text-accent">
                          {tutorial.title}
                        </h3>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-6 h-6 text-ink transition-transform duration-300 flex-shrink-0 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Accordion Content */}
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      isOpen
                        ? 'max-h-[800px] opacity-100'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pt-2">
                      {/* Description */}
                      <p className="text-ink mb-6 leading-relaxed">
                        {tutorial.description}
                      </p>

                      {/* Video Player */}
                      <div className="relative rounded-ns overflow-hidden bg-ink shadow-pop">
                        <video
                          controls
                          className="w-full h-auto"
                          preload="metadata"
                        >
                          <source src={tutorial.videoUrl} type="video/mp4" />
                          <source src={tutorial.videoUrl} type="video/quicktime" />
                          Your browser does not support the video tag.
                        </video>
                      </div>

                      {/* Video Info + Resource Link */}
                      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                        <div className="px-3 py-1 bg-shade-50 rounded-full text-sm text-ink">
                          Video Tutorial
                        </div>
                        {tutorial.resourceLink && (
                          <a
                            href={tutorial.resourceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-ink bg-shade-50 border border-rule-strong rounded-ns  transition-all duration-200"
                          >
                            Click here for details
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })}
        </div>

        {/* Help Footer */}
        <div className="mt-12 p-6 bg-white rounded-ns-tile  border border-rule">
          <h3 className="text-base font-semibold text-ink mb-2">
            Need more help?
          </h3>
          <p className="text-ink mb-4">
            Can't find what you're looking for? Our support team is here to help you get started.
          </p>
          <a 
            href="https://www.nirvascribe.com/contact-us" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-2 bg-shade-50 text-ink rounded-ns font-medium hover:bg-opacity-90 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;